import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useViewTop } from '../ui/revealForm';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import HelpButton from '../tours/HelpButton';
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast } from '../utils/toast';   // Block 233
import { uploadStoredFile } from '../utils/storedFiles';

const NAVY   = '#0C2D48';
const BORDER = '#e0e0e0';
const BG     = '#f8f9fb';

const PRESET_COLOURS = [
  '#b0bec5', '#E4E69B', '#4fc3f7', '#81c784',
  '#ef9a9a', '#ce93d8', '#ffcc80', '#80cbc4',
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtShort(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const BLANK_ENTRY = { title: '', body: '', entryDate: todayISO(), categoryId: '' };

// Must match THUMB_MAX_LEN in journal.js backend so the card knows when to show placeholder.
const THUMB_MAX_LEN = 204800;

function computeListImageFields(images) {
  const arr = Array.isArray(images) ? images : [];
  const first = arr[0];
  return {
    imageCount:      arr.length,
    // B4: S3 URL is tiny — always include; legacy base64 — keep 150 KB gate
    firstImageThumb: first
        ? (first.url ? first.url : (first.dataUri && first.dataUri.length <= THUMB_MAX_LEN ? first.dataUri : null))
        : null,
  };
}

export default function Journal() {
  const { activeWorkspaceId, workspaces } = useCurrentUser();
  const isPersonalWs = (workspaces || []).find(w => w.id === activeWorkspaceId)?.type === 'personal';

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  const [entries, setEntries]         = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile);
  const [viewFilter, setViewFilter]   = useState('All');
  const [search, setSearch]           = useState('');
  const [sortBy, setSortBy]           = useState('date');

  const [openEntry, setOpenEntry]     = useState(null); // null = list, object = editor
  // An editor that replaces the page opens at its top, not at the list's scroll (2026-10-21).
  useViewTop(openEntry ? (openEntry.id || 'new') : null);
  const [isNew, setIsNew]             = useState(false);
  const [form, setForm]               = useState(BLANK_ENTRY);
  const [saving, setSaving]           = useState(false);
  const [savedFlash, setSavedFlash]   = useState(false);
  const [entryImages, setEntryImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError]   = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Resolved image URLs (presigned for private `key` entries; stored URL for
  // legacy `url` entries). Indexed to match entryImages.
  const [imageUrls, setImageUrls] = useState([]);

  async function loadImageUrls(entryId) {
    if (!entryId) return;
    try {
      const r = await api(`/api/journal/${entryId}/image-urls`);
      const d = await r.json();
      setImageUrls(Array.isArray(d.images) ? d.images.map(i => i.url) : []);
    } catch {
      /* a failed image load is not 'this entry has no images' */
    }
  }

  const [hoveredId, setHoveredId]     = useState(null);
  const saveTimerRef = useRef(null);
  // Carla's item 4 (2026-10-21): the autosave ended .catch(() => {}), so a blocked or failed save was
  // never said. saveStatus is said beside Save; unsavedRef keeps, per entry, the words whose save
  // failed, so closing and reopening shows them (and the warning) rather than the older stored text.
  const [saveStatus, setSaveStatus] = useState({ status: 'idle', message: '' });
  const unsavedRef = useRef(new Map());
  const openIdRef = useRef(null);
  const imageFileRef = useRef(null);

  // ── Category sidebar state ────────────────────────────────────────────────
  const [hoveredCatId, setHoveredCatId]   = useState(null);
  const [editingCatId, setEditingCatId]   = useState(null);
  const [editCatName, setEditCatName]     = useState('');
  const [editCatColour, setEditCatColour] = useState('');
  const [confirmDelCatId, setConfirmDelCatId] = useState(null);
  const [showNewCat, setShowNewCat]       = useState(false);
  const [newCatName, setNewCatName]       = useState('');
  const [newCatColour, setNewCatColour]   = useState(PRESET_COLOURS[1]);
  const [showManageCats, setShowManageCats] = useState(false);
  const [mgmtEditId, setMgmtEditId]       = useState(null);
  const [mgmtName, setMgmtName]           = useState('');
  const [mgmtColour, setMgmtColour]       = useState('');
  const [mgmtConfDel, setMgmtConfDel]     = useState(null);
  const [confirmDelEntryId, setConfirmDelEntryId] = useState(null);
  const [confirmDelForm,    setConfirmDelForm]    = useState(false);

  // ── Inline category mode inside editor ───────────────────────────────────
  const [editorCatMode, setEditorCatMode]       = useState('select');
  const [editorNewCatName, setEditorNewCatName] = useState('');
  const [editorNewCatCol, setEditorNewCatCol]   = useState(PRESET_COLOURS[1]);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!activeWorkspaceId || !isPersonalWs) return;
    setLoading(true);
    setError('');
    try {
      const [eRes, cRes] = await Promise.all([
        api('/api/journal'),
        api('/api/journal-categories'),
      ]);
      if (!eRes.ok || !cRes.ok) throw new Error('Load failed');
      const [ed, cd] = await Promise.all([eRes.json(), cRes.json()]);
      setEntries(ed.entries || []);
      setCategories(cd.categories || []);
    } catch {
      setError('Could not load journal.');
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, isPersonalWs]);

  useEffect(() => { load(); }, [load]);

  // ── Category helpers ──────────────────────────────────────────────────────
  const catById  = useCallback(id => categories.find(c => c.id === id) || null, [categories]);
  const catName  = e => catById(e?.categoryId)?.name  || 'General';
  const catColour = e => catById(e?.categoryId)?.colour || '#b0bec5';

  // ── Category CRUD ─────────────────────────────────────────────────────────
  async function createCat(name, colour) {
    if (!name.trim()) return null;
    try {
      const res  = await api('/api/journal-categories', { method: 'POST', body: JSON.stringify({ name: name.trim(), colour }) });
      const data = await res.json();
      if (data.category) { setCategories(prev => [...prev, data.category]); return data.category; }
    } catch (err) { showErrorToast(messageFromError(err, 'Could not create that category.')); }
    return null;
  }

  async function updateCat(id, name, colour) {
    try {
      const res  = await api(`/api/journal-categories/${id}`, { method: 'PUT', body: JSON.stringify({ name, colour }) });
      const data = await res.json();
      if (data.category) setCategories(prev => prev.map(c => c.id === id ? data.category : c));
    } catch (err) { showErrorToast(messageFromError(err, "Could not rename that category.")); }
  }

  async function deleteCat(id) {
    try {
      await api(`/api/journal-categories/${id}`, { method: 'DELETE' });
      setCategories(prev => prev.filter(c => c.id !== id));
      setEntries(prev => prev.map(e => e.categoryId === id ? { ...e, categoryId: null } : e));
      if (viewFilter === id) setViewFilter('All');
    } catch (err) { showErrorToast(messageFromError(err, "Could not delete that category.")); }
  }

  // ── Entry CRUD ────────────────────────────────────────────────────────────
  function openNew() {
    setIsNew(true);
    openIdRef.current = null;
    setSaveStatus({ status: 'idle', message: '' });
    setForm({ ...BLANK_ENTRY, entryDate: todayISO(), categoryId: viewFilter !== 'All' ? viewFilter : '' });
    setOpenEntry({});
    setEditorCatMode('select');
    setEntryImages([]);
    setImageError('');
  }

  function openEdit(entry) {
    setIsNew(false);
    const kept = unsavedRef.current.get(entry.id);
    openIdRef.current = entry.id;
    setSaveStatus(kept ? { status: 'failed', message: kept.message } : { status: 'idle', message: '' });
    setForm({
      title:      entry.title || '',
      body:       kept ? kept.body : (entry.body || ''),
      entryDate:  entry.entryDate ? entry.entryDate.slice(0, 10) : todayISO(),
      categoryId: entry.categoryId || '',
    });
    setOpenEntry(entry);
    setEditorCatMode('select');
    setEntryImages([]);   // start empty; full images load below
    setImageError('');
    // List entries no longer carry full images — fetch the single entry to get them.
    if (entry.id) {
      api(`/api/journal/${entry.id}`)
        .then(r => r.json())
        .then(d => {
          if (Array.isArray(d.entry?.images)) setEntryImages(d.entry.images);
          loadImageUrls(entry.id);
        })
        .catch(() => {});
    }
  }

  async function saveEntry() {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        title:      form.title.trim() || null,
        body:       form.body.trim(),
        entryDate:  form.entryDate,
        categoryId: form.categoryId || null,
      };
      if (!payload.body) { setSaving(false); return; }

      if (isNew) {
        const res  = await api('/api/journal', { method: 'POST', body: JSON.stringify(payload) });
        const data = await res.json();
        if (res.ok && data.entry) {
          setEntries(prev => [data.entry, ...prev]);
          setSavedFlash(true);
          setTimeout(() => { setSavedFlash(false); setSaving(false); setOpenEntry(null); }, 700);
          return;
        }
      } else {
        const res  = await api(`/api/journal/${openEntry.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        const data = await res.json();
        if (res.ok && data.entry) {
          setEntries(prev => prev.map(e => e.id === data.entry.id ? { ...e, ...data.entry } : e));
          unsavedRef.current.delete(data.entry.id);
          setSaveStatus({ status: 'idle', message: '' });
          setSavedFlash(true);
          setTimeout(() => { setSavedFlash(false); setSaving(false); setOpenEntry(null); }, 700);
          return;
        }
      }
      // An answer with no entry in it: not saved, and said so.
      throw new Error('Aidey sent back something it could not read, so it may not have saved.');
    } catch (err) {
      setSaving(false);
      setSaveStatus({ status: 'failed', message: messageFromError(err, 'Could not save that entry.') });
    }
  }

  async function deleteEntry(id) {
    try {
      await api(`/api/journal/${id}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.id !== id));
      setOpenEntry(null);
    } catch (err) { showErrorToast(messageFromError(err, 'Could not delete that entry.')); }
  }

  // ── Image upload / delete ─────────────────────────────────────────────────
  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !openEntry?.id || uploadingImage) return;
    e.target.value = '';
    setImageError('');
    setUploadingImage(true);
    try {
      // Into the private file store (kind 'journal_entry'), then added to the entry by id (Carla's
      // decision 2): the old multipart upload was refused at the edge for any real photo.
      const stored = await uploadStoredFile({ kind: 'journal_entry', entityId: openEntry.id, file });
      const res = await api(`/api/journal/${openEntry.id}/images`, { method: 'POST', body: JSON.stringify({ fileId: stored.id }) });
      const data = await res.json();
      const images = Array.isArray(data.entry?.images) ? data.entry.images : [];
      setEntryImages(images);
      loadImageUrls(openEntry.id);
      setEntries(prev => prev.map(e => e.id === openEntry.id ? { ...e, ...computeListImageFields(images) } : e));
      setOpenEntry(prev => ({ ...prev, images }));
    } catch (err) {
      setImageError(messageFromError(err, 'Upload failed. Please try again.'));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleImageDelete(idx) {
    if (!openEntry?.id) return;
    setImageError('');
    try {
      const res = await api(`/api/journal/${openEntry.id}/images/${idx}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) { setImageError(data.error || 'Delete failed.'); return; }
      const images = Array.isArray(data.entry?.images) ? data.entry.images : [];
      setEntryImages(images);
      loadImageUrls(openEntry.id);
      setEntries(prev => prev.map(e => e.id === openEntry.id ? { ...e, ...computeListImageFields(images) } : e));
      setOpenEntry(prev => ({ ...prev, images }));
      if (lightboxIdx !== null && lightboxIdx >= images.length) setLightboxIdx(null);
    } catch {
      setImageError('Failed to remove image.');
    }
  }

  // ── Auto-save on body change (edit mode only) ─────────────────────────────
  function handleBodyChange(val) {
    setForm(f => ({ ...f, body: val }));
    if (isNew) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    const id = openEntry?.id;
    saveTimerRef.current = setTimeout(async () => {
      if (!id) return;
      const here = () => openIdRef.current === id;
      if (here()) setSaveStatus({ status: 'saving', message: '' });
      try {
        const res = await api(`/api/journal/${id}`, { method: 'PATCH', body: JSON.stringify({ body: val }) });
        const data = await res.json().catch(() => null);
        unsavedRef.current.delete(id);
        // The list holds what is stored now, so reopening shows these words.
        setEntries(prev => prev.map(e => (e.id === id ? { ...e, body: val, ...(data?.entry ? { updatedAt: data.entry.updatedAt } : {}) } : e)));
        if (here()) setSaveStatus({ status: 'saved', message: '' });
      } catch (err) {
        const message = messageFromError(err, 'Could not save that entry.');
        unsavedRef.current.set(id, { body: val, message });
        if (here()) setSaveStatus({ status: 'failed', message });
        else showErrorToast(`Couldn't save your journal entry, your text is kept: open it again to see it. ${message}`);
      }
    }, 1500);
  }

  // ── Editor: inline new category ───────────────────────────────────────────
  async function handleEditorCatChange(val) {
    if (val === '__new__') {
      setEditorCatMode('new');
      setEditorNewCatName('');
      setEditorNewCatCol(PRESET_COLOURS[1]);
    } else {
      setEditorCatMode('select');
      setForm(f => ({ ...f, categoryId: val }));
    }
  }

  async function confirmEditorNewCat() {
    if (!editorNewCatName.trim()) return;
    const cat = await createCat(editorNewCatName.trim(), editorNewCatCol);
    if (cat) { setEditorCatMode('select'); setForm(f => ({ ...f, categoryId: cat.id })); }
  }

  // ── Filtering & sorting ───────────────────────────────────────────────────
  const filtered = useMemo(() => entries.filter(e => {
    const q = search.toLowerCase();
    const matchText = !q ||
      (e.body  || '').toLowerCase().includes(q) ||
      (e.title || '').toLowerCase().includes(q);
    const matchCat = viewFilter === 'All' || (e.categoryId || null) === viewFilter;
    return matchText && matchCat;
  }), [entries, search, viewFilter]);

  const sorted = useMemo(() => {
    const base = [...filtered];
    if (sortBy === 'date')  return base.sort((a, b) => (b.entryDate || '').localeCompare(a.entryDate || '') || (b.createdAt || '').localeCompare(a.createdAt || ''));
    if (sortBy === 'alpha') return base.sort((a, b) => (a.title || a.body || '').localeCompare(b.title || b.body || ''));
    if (sortBy === 'category') return base.sort((a, b) => catName(a).localeCompare(catName(b)) || (b.entryDate || '').localeCompare(a.entryDate || ''));
    return base;
  }, [filtered, sortBy, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Editor view ───────────────────────────────────────────────────────────
  if (openEntry !== null) {
    return (
      <div style={{ minHeight: '80vh', fontFamily: 'Raleway, sans-serif' }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.75rem 1.5rem', borderBottom: `1px solid ${BORDER}`,
          background: '#fff', position: 'sticky', top: 0, zIndex: 'var(--z-sticky)',
        }}>
          <button
            onClick={() => { openIdRef.current = null; setOpenEntry(null); setEditorCatMode('select'); }}
            style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '0.35rem 0.85rem', cursor: 'pointer', fontSize: '0.88rem', color: '#555' }}
          >
            ← Back
          </button>

          {/* Date */}
          <input
            type="date"
            value={form.entryDate}
            onChange={e => setForm(f => ({ ...f, entryDate: e.target.value }))}
            style={{ padding: '0.3rem 0.55rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem' }}
          />

          {/* Category selector */}
          {editorCatMode === 'select' ? (
            <select
              value={form.categoryId}
              onChange={e => handleEditorCatChange(e.target.value)}
              style={{ padding: '0.35rem 0.65rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem', background: '#fff' }}
            >
              <option value="">General</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="__new__">＋ New Category…</option>
            </select>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <input
                autoFocus
                type="text"
                placeholder="Category name…"
                value={editorNewCatName}
                onChange={e => setEditorNewCatName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmEditorNewCat(); if (e.key === 'Escape') setEditorCatMode('select'); }}
                style={{ padding: '0.3rem 0.55rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.85rem', width: 130 }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                {PRESET_COLOURS.map(col => (
                  <button key={col} onClick={() => setEditorNewCatCol(col)} style={{ width: 18, height: 18, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: editorNewCatCol === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }} />
                ))}
              </div>
              <button onClick={confirmEditorNewCat} style={{ padding: '0.3rem 0.7rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>Add</button>
              <button onClick={() => setEditorCatMode('select')} style={{ padding: '0.3rem 0.55rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer' }}>✕</button>
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            onClick={saveEntry}
            disabled={saving || !form.body.trim()}
            style={{
              background: savedFlash ? '#27ae60' : NAVY, color: '#fff', border: 'none',
              borderRadius: 6, padding: '0.35rem 0.9rem', cursor: (saving || !form.body.trim()) ? 'default' : 'pointer',
              fontSize: '0.88rem', fontWeight: 700, transition: 'background 0.2s', minWidth: 72,
              opacity: !form.body.trim() ? 0.5 : 1,
            }}
          >
            {savedFlash ? 'Saved ✓' : saving ? 'Saving…' : 'Save'}
          </button>
          {/* THE SAVE STATUS: Saving…, Saved, or that it did not save. Never "Saved" after a failure. */}
          <span data-testid="journal-save-status" role={saveStatus.status === 'failed' ? 'alert' : undefined} aria-live="polite"
            style={{ fontSize: '0.82rem', fontWeight: 600, color: saveStatus.status === 'failed' ? '#b42318' : '#667085' }}>
            {saveStatus.status === 'saving' ? 'Saving…' : saveStatus.status === 'saved' ? 'Saved'
              : saveStatus.status === 'failed' ? `Couldn't save, your text is kept here. ${saveStatus.message}` : ''}
          </span>

          {!isNew && (
            <>
              <input
                ref={imageFileRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => imageFileRef.current?.click()}
                disabled={uploadingImage || entryImages.length >= 5}
                title={entryImages.length >= 5 ? 'Max 5 images' : 'Attach photo'}
                style={{
                  background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
                  padding: '0.35rem 0.7rem', cursor: uploadingImage || entryImages.length >= 5 ? 'not-allowed' : 'pointer',
                  fontSize: '0.82rem', color: '#666',
                  opacity: entryImages.length >= 5 ? 0.4 : 1,
                }}
              >
                {uploadingImage ? '↑ Uploading…' : '📷 Photo'}
              </button>
            </>
          )}

          {!isNew && (
            confirmDelForm ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                <span style={{ color: '#E96A6A' }}>Delete this entry?</span>
                <button onClick={() => { deleteEntry(openEntry.id); setConfirmDelForm(false); }} style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.82rem', color: '#E96A6A', fontWeight: 700 }}>Yes</button>
                <button onClick={() => setConfirmDelForm(false)} style={{ background: 'none', border: '1px solid #dde3ea', borderRadius: 6, padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.82rem', color: '#6b7280' }}>No</button>
              </span>
            ) : (
              <button
                onClick={() => setConfirmDelForm(true)}
                style={{ background: 'none', border: '1px solid #fcc', borderRadius: 6, padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.82rem', color: '#E96A6A' }}
              >
                Delete
              </button>
            )
          )}
        </div>

        {/* Entry editor body */}
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem' }}>
          <input
            type="text"
            placeholder="Title (optional)"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            style={{
              width: '100%', border: 'none', outline: 'none', fontSize: '1.5rem',
              fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: NAVY,
              marginBottom: '1rem', background: 'transparent', boxSizing: 'border-box',
            }}
          />
          <textarea
            autoFocus
            placeholder="Write your entry…"
            value={form.body}
            onChange={e => handleBodyChange(e.target.value)}
            style={{
              width: '100%', minHeight: '60vh', border: 'none', outline: 'none',
              fontSize: '1rem', fontFamily: 'Raleway, sans-serif', lineHeight: 1.8,
              color: '#333', resize: 'none', background: 'transparent', boxSizing: 'border-box',
            }}
          />

          {/* Image strip */}
          {imageError && <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '0.5rem' }}>{imageError}</p>}
          {entryImages.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${BORDER}` }}>
              {entryImages.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={imageUrls[idx] || img.url || img.dataUri}
                    alt={img.fileName || `Image ${idx + 1}`}
                    onClick={() => setLightboxIdx(idx)}
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: `1px solid ${BORDER}`, display: 'block' }}
                  />
                  <button
                    onClick={() => handleImageDelete(idx)}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                    title="Remove image"
                  >×</button>
                </div>
              ))}
              {entryImages.length < 5 && (
                <button
                  onClick={() => imageFileRef.current?.click()}
                  disabled={uploadingImage}
                  style={{ width: 80, height: 80, borderRadius: 8, border: `2px dashed ${BORDER}`, background: '#f8f9fb', cursor: uploadingImage ? 'not-allowed' : 'pointer', color: '#aaa', fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >+</button>
              )}
            </div>
          )}
        </div>

        {/* Lightbox */}
        {lightboxIdx !== null && entryImages[lightboxIdx] && (
          <div
            onClick={() => setLightboxIdx(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 'var(--z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          >
            <img
              src={imageUrls[lightboxIdx] || entryImages[lightboxIdx].url || entryImages[lightboxIdx].dataUri}
              alt={entryImages[lightboxIdx].fileName || 'Image'}
              style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
              onClick={e => e.stopPropagation()}
            />
            <button
              onClick={() => setLightboxIdx(null)}
              style={{ position: 'fixed', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >✕</button>
          </div>
        )}
      </div>
    );
  }

  // ── Two-panel list view ───────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      minHeight: isMobile ? 'auto' : 'calc(100vh - 4rem)',
      margin: isMobile ? '0' : '-2rem',
      fontFamily: 'Raleway, sans-serif',
      background: BG,
      overflow: isMobile ? 'visible' : 'hidden',
      position: 'relative',
    }}>

      {/* ── Manage Categories Modal ────────────────────────────────────────── */}
      {showManageCats && (
        <div onClick={() => { setShowManageCats(false); setMgmtEditId(null); setMgmtConfDel(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 'var(--z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: '1.5rem', width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '1rem', color: NAVY }}>Manage Categories</h3>
              <button onClick={() => setShowManageCats(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#aaa' }}>✕</button>
            </div>

            {categories.length === 0 && <p style={{ color: '#aaa', fontSize: '0.88rem', textAlign: 'center', padding: '1rem 0' }}>No categories yet.</p>}

            {categories.map(cat => (
              <div key={cat.id} style={{ marginBottom: '0.5rem' }}>
                {mgmtEditId === cat.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.6rem', background: '#f8f9fb', borderRadius: 8 }}>
                    <input autoFocus value={mgmtName} onChange={e => setMgmtName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { updateCat(cat.id, mgmtName, mgmtColour); setMgmtEditId(null); } if (e.key === 'Escape') setMgmtEditId(null); }}
                      style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.88rem' }} />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {PRESET_COLOURS.map(col => (
                        <button key={col} onClick={() => setMgmtColour(col)} style={{ width: 20, height: 20, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: mgmtColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }} />
                      ))}
                      <div style={{ flex: 1 }} />
                      <button onClick={() => { updateCat(cat.id, mgmtName, mgmtColour); setMgmtEditId(null); }} style={{ padding: '0.3rem 0.75rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setMgmtEditId(null)} style={{ padding: '0.3rem 0.65rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : mgmtConfDel === cat.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', background: '#fff3f3', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.83rem', color: '#555', flex: 1 }}>Delete "{cat.name}"?</span>
                    <button onClick={() => { deleteCat(cat.id); setMgmtConfDel(null); }} style={{ padding: '0.28rem 0.75rem', background: '#E96A6A', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                    <button onClick={() => setMgmtConfDel(null)} style={{ padding: '0.28rem 0.65rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', borderRadius: 8, background: '#fafaf8' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.colour, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: '0.9rem', color: NAVY }}>{cat.name}</span>
                    <span style={{ fontSize: '0.75rem', color: '#bbb' }}>{entries.filter(e => e.categoryId === cat.id).length}</span>
                    <button onClick={() => { setMgmtEditId(cat.id); setMgmtName(cat.name); setMgmtColour(cat.colour); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '0.82rem', padding: '2px 6px' }} title="Edit">✏️</button>
                    <button onClick={() => setMgmtConfDel(cat.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E96A6A', fontSize: '0.82rem', padding: '2px 6px' }} title="Delete">🗑️</button>
                  </div>
                )}
              </div>
            ))}

            <div style={{ marginTop: '1rem', borderTop: `1px solid ${BORDER}`, paddingTop: '1rem' }}>
              <AddCatInline onAdd={createCat} />
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile sidebar overlay ─────────────────────────────────────────── */}
      <div className={`notes-sidebar-overlay${sidebarOpen ? ' overlay-visible' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* ── Sidebar ───────────────────────────────────────────────────────── */}
      <div className={`notes-sidebar-drawer${sidebarOpen ? ' sidebar-open' : ''}`}
        style={{ width: isMobile ? '82vw' : 240, maxWidth: isMobile ? 320 : 'none', background: '#fff', borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
        <div style={{ padding: '18px 14px 12px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 800, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Journal</span>
            <button data-tour-id="journal-new" onClick={openNew} style={{ background: '#E4E69B', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: NAVY }}>+ New</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {/* All Entries */}
          <div onClick={() => { setViewFilter('All'); if (window.innerWidth < 768) setSidebarOpen(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', background: viewFilter === 'All' ? `${NAVY}0d` : 'transparent', marginBottom: 2 }}>
            <span style={{ fontSize: 14 }}>📓</span>
            <span style={{ fontSize: 13, fontWeight: viewFilter === 'All' ? 700 : 400, color: NAVY, flex: 1 }}>All Entries</span>
            <span style={{ fontSize: 11, color: '#aaa' }}>{entries.length}</span>
          </div>
          <div style={{ height: 1, background: BORDER, margin: '6px 4px' }} />

          {categories.map(cat => {
            const count   = entries.filter(e => e.categoryId === cat.id).length;
            const isActive = viewFilter === cat.id;
            const isHov    = hoveredCatId === cat.id;
            const isEdit   = editingCatId === cat.id;
            const isConfDel = confirmDelCatId === cat.id;

            return (
              <div key={cat.id}
                onMouseEnter={() => setHoveredCatId(cat.id)}
                onMouseLeave={() => setHoveredCatId(null)}
                style={{ borderRadius: 8, background: isActive ? `${NAVY}0d` : 'transparent', marginBottom: 1 }}>
                {isEdit ? (
                  <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <input autoFocus value={editCatName} onChange={e => setEditCatName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { updateCat(cat.id, editCatName, editCatColour); setEditingCatId(null); } if (e.key === 'Escape') setEditingCatId(null); }}
                      style={{ padding: '0.28rem 0.5rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.82rem', width: '100%', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {PRESET_COLOURS.map(col => <button key={col} onClick={() => setEditCatColour(col)} style={{ width: 16, height: 16, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: editCatColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }} />)}
                    </div>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button onClick={() => { updateCat(cat.id, editCatName, editCatColour); setEditingCatId(null); }} style={{ flex: 1, padding: '0.28rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setEditingCatId(null)} style={{ padding: '0.28rem 0.5rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer' }}>✕</button>
                    </div>
                  </div>
                ) : isConfDel ? (
                  <div style={{ padding: '6px 8px' }}>
                    <p style={{ margin: '0 0 0.35rem', fontSize: '0.78rem', color: '#555' }}>Delete "{cat.name}"?</p>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button onClick={() => { deleteCat(cat.id); setConfirmDelCatId(null); }} style={{ flex: 1, padding: '0.28rem', background: '#E96A6A', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                      <button onClick={() => setConfirmDelCatId(null)} style={{ padding: '0.28rem 0.5rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div onClick={() => { setViewFilter(cat.id); if (window.innerWidth < 768) setSidebarOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, padding: '7px 8px', cursor: 'pointer', minWidth: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.colour, flexShrink: 0, border: '1px solid rgba(0,0,0,0.08)' }} />
                      <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: NAVY, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</span>
                      <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>{count}</span>
                    </div>
                    {isHov && (
                      <div style={{ display: 'flex', gap: 2, paddingRight: 4 }}>
                        <button onClick={e => { e.stopPropagation(); setEditingCatId(cat.id); setEditCatName(cat.name); setEditCatColour(cat.colour); setConfirmDelCatId(null); }} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '2px 4px', color: '#888', borderRadius: 4 }}>✏️</button>
                        <button onClick={e => { e.stopPropagation(); setConfirmDelCatId(cat.id); setEditingCatId(null); }} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '2px 4px', color: '#E96A6A', borderRadius: 4 }}>🗑️</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 12px', borderTop: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {showNewCat ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <input type="text" placeholder="Category name…" value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter') { if (newCatName.trim()) await createCat(newCatName.trim(), newCatColour); setShowNewCat(false); setNewCatName(''); }
                  if (e.key === 'Escape') { setShowNewCat(false); setNewCatName(''); }
                }}
                autoFocus
                style={{ padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {PRESET_COLOURS.map(col => <button key={col} onClick={() => setNewCatColour(col)} style={{ width: 18, height: 18, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: newCatColour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }} />)}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={async () => { if (newCatName.trim()) await createCat(newCatName.trim(), newCatColour); setShowNewCat(false); setNewCatName(''); }}
                  style={{ flex: 1, padding: '0.35rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>Add</button>
                <button onClick={() => { setShowNewCat(false); setNewCatName(''); }}
                  style={{ padding: '0.35rem 0.6rem', background: '#f1f1f1', border: 'none', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNewCat(true)}
              style={{ width: '100%', padding: '0.4rem', background: 'none', border: `1px dashed ${BORDER}`, borderRadius: 8, fontSize: '0.82rem', color: '#aaa', cursor: 'pointer', fontWeight: 600 }}>
              + New Category
            </button>
          )}
          <button onClick={() => setShowManageCats(true)}
            style={{ width: '100%', padding: '0.38rem', background: 'none', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}>
            ⚙ Manage Categories
          </button>
        </div>
      </div>

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <div className="notes-main-content" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: isMobile ? '14px 12px 12px' : '18px 24px 14px', background: '#fff', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10 }}>
          <button className="notes-sidebar-toggle" onClick={() => setSidebarOpen(v => !v)} aria-label="Toggle categories">Categories</button>
          <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '1.1rem', color: NAVY }}>
            {viewFilter === 'All' ? 'All Entries' : (categories.find(c => c.id === viewFilter)?.name || viewFilter)}
          </h2>
          <HelpButton tourId="journal" />
          <span style={{ fontSize: '0.78rem', color: '#aaa', marginRight: 'auto' }}>{sorted.length} {sorted.length === 1 ? 'entry' : 'entries'}</span>
          <input type="text" placeholder="Search journal…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '0.45rem 0.8rem', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: '0.88rem', minWidth: isMobile ? 0 : 180, width: isMobile ? '100%' : 'auto', flex: isMobile ? '1 1 100%' : '0 1 auto', boxSizing: 'border-box' }} />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: '0.88rem', background: '#fff', cursor: 'pointer' }}>
            <option value="date">Date</option>
            <option value="alpha">Alphabetical</option>
            <option value="category">Category</option>
          </select>
        </div>

        <div style={{ padding: isMobile ? '12px' : '20px 24px', flex: 1 }}>
          {!activeWorkspaceId && <p style={{ color: '#bbb', fontSize: 14 }}>Connecting to workspace…</p>}
          {activeWorkspaceId && loading && <p style={{ color: '#aaa', fontSize: 14 }}>Loading…</p>}
          {error  && <p style={{ color: '#dc2626', fontSize: 14 }}>{error}</p>}

          {!loading && sorted.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem 0', color: '#bbb' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📓</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#aaa' }}>No entries here</div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.35rem', color: '#ccc' }}>
                Click{' '}
                <span onClick={openNew} style={{ color: NAVY, cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>+ New</span>
                {' '}to write your first entry.
              </div>
            </div>
          )}

          {!loading && sorted.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))', gap: isMobile ? '0.75rem' : '1rem' }}>
              {sorted.map(entry => {
                const isHov    = hoveredId === entry.id;
                const hasTitle = !!entry.title?.trim();
                const title    = hasTitle ? entry.title.trim() : (entry.body?.trim().split(/\s+/).slice(0, 6).join(' ') || 'Untitled');
                const preview  = hasTitle ? entry.body.slice(0, 130) : (entry.body?.trim().split(/\s+/).slice(6).join(' ') || '').slice(0, 110);
                const cc       = catColour(entry);
                const cn       = catName(entry);
                const firstImg = entry.firstImageThumb || null;
                const imgCount = entry.imageCount || 0;

                return (
                  <div key={entry.id}
                    onMouseEnter={() => setHoveredId(entry.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => openEdit(entry)}
                    style={{ background: '#fff', border: `1px solid ${isHov ? `${NAVY}30` : BORDER}`, borderRadius: 12, padding: 0, cursor: 'pointer', position: 'relative', transition: 'box-shadow 0.15s, border-color 0.15s', boxShadow: isHov ? '0 4px 16px rgba(12,45,72,0.1)' : '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', minHeight: 140, overflow: 'hidden' }}>
                    {/* First image thumbnail banner */}
                    {firstImg ? (
                      <img src={firstImg} alt="" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block', borderRadius: '12px 12px 0 0' }} />
                    ) : imgCount > 0 ? (
                      <div style={{ width: '100%', height: 60, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px 12px 0 0', fontSize: '1.5rem' }}>📷</div>
                    ) : null}

                    <div style={{ padding: '0.85rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>

                    {isHov && (
                      <div style={{ position: 'absolute', top: firstImg ? 108 : (imgCount > 0 ? 68 : 8), right: 8, display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
                        <button onClick={e => { e.stopPropagation(); openEdit(entry); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: NAVY, fontWeight: 700 }}>Edit</button>
                        {confirmDelEntryId === entry.id ? (
                          <>
                            <span style={{ fontSize: '0.72rem', color: '#E96A6A', alignSelf: 'center' }}>Delete?</span>
                            <button onClick={e => { e.stopPropagation(); deleteEntry(entry.id); setConfirmDelEntryId(null); }} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: '#E96A6A', fontWeight: 700 }}>Yes</button>
                            <button onClick={e => { e.stopPropagation(); setConfirmDelEntryId(null); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: '#555', fontWeight: 700 }}>No</button>
                          </>
                        ) : (
                          <button onClick={e => { e.stopPropagation(); setConfirmDelEntryId(entry.id); }} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontSize: '0.72rem', color: '#E96A6A', fontWeight: 700 }}>Delete</button>
                        )}
                      </div>
                    )}
                    <div style={{ paddingRight: isHov ? 90 : 0 }}>
                      <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: NAVY, lineHeight: 1.35 }}>{title}</span>
                    </div>
                    {preview && (
                      <p style={{ margin: 0, fontSize: '0.81rem', color: '#666', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{preview}</p>
                    )}
                    <div style={{ flex: 1 }} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: cc + '28', border: `1px solid ${cc}66`, borderRadius: 20, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700, color: '#555', maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cc, display: 'inline-block', flexShrink: 0 }} />
                        {cn}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#bbb', flexShrink: 0 }}>{fmtShort(entry.entryDate)}</span>
                    </div>
                    {imgCount > 1 && (
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>📷 {imgCount} photos</div>
                    )}

                    </div>{/* end inner padding div */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddCatInline({ onAdd }) {
  const [name, setName]     = useState('');
  const [colour, setColour] = useState(PRESET_COLOURS[1]);
  const [busy, setBusy]     = useState(false);

  async function submit() {
    if (!name.trim() || busy) return;
    setBusy(true);
    await onAdd(name.trim(), colour);
    setName(''); setColour(PRESET_COLOURS[1]); setBusy(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <input type="text" placeholder="New category name…" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        style={{ padding: '0.45rem 0.65rem', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: '0.9rem' }} />
      <div style={{ display: 'flex', gap: 6 }}>
        {PRESET_COLOURS.map(col => <button key={col} onClick={() => setColour(col)} style={{ width: 22, height: 22, borderRadius: '50%', background: col, border: 'none', cursor: 'pointer', outline: colour === col ? `2px solid ${NAVY}` : 'none', outlineOffset: 1 }} />)}
      </div>
      <button onClick={submit} disabled={!name.trim() || busy}
        style={{ padding: '0.45rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontWeight: 700, cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5 }}>
        + Add Category
      </button>
    </div>
  );
}
